import * as React from 'react';

import { Icon } from 'components/atoms/Icon/Icon';
import UIAsset from 'components/atoms/UIAsset';
import { IdentifyGoalType, isUnderWeightByBMI } from 'components/molecules/HealthGoal/goal-selection';
import i18n from 'helper/i18n';
import noop from 'helper/noop';

export const GOAL = {
	fruits: 'fruits',
	running: 'running',
	healthier: 'healthier',
} as const;

export type TGoal = keyof typeof GOAL;

export type HealthGoalItemWithoutType = {
	name: TGoal;
	text: string;
};

export type HealthGoalItem = HealthGoalItemWithoutType & {
	goalType: IdentifyGoalType;
};

export type HealthGoalSelectionProps = {
	selectedGoal?: TGoal;
	goals: HealthGoalItem[];
	text?: string;
	bmiValue: number;
	isPregnant?: boolean;
	onGoalChange: (selectedGoal: TGoal) => void;
};

export type HealthGoalTypesType = 'GETTING_FIT' | 'LOSE_WEIGHT' | 'HEALTHY_LIFESTYLE';

type ExplanationLabelKeys =
	| 'title'
	| 'item1.title'
	| 'item1.iconName'
	| 'item1.text'
	| 'item2.title'
	| 'item2.iconName'
	| 'item2.text'
	| 'pregnant.text'
	| 'underweight.text';

export const HealthGoalTypes = {
	gettingFit: 'GETTING_FIT',
	loseWeight: 'LOSE_WEIGHT',
	healthyLifestyle: 'HEALTHY_LIFESTYLE',
} as const;

export function getHealthGoalTypesFromGoal(goal?: TGoal): HealthGoalTypesType {
	switch (goal) {
		case GOAL.running:
			return HealthGoalTypes.gettingFit;
		case GOAL.fruits:
			return HealthGoalTypes.loseWeight;
		default:
			return HealthGoalTypes.healthyLifestyle;
	}
}

export function getGoalFromHealthGoalTypes(healthGoalType?: HealthGoalTypesType): TGoal {
	switch (healthGoalType) {
		case HealthGoalTypes.gettingFit:
			return GOAL.running;
		case HealthGoalTypes.loseWeight:
			return GOAL.fruits;
		default:
			return GOAL.healthier;
	}
}

export function getImagePathFromGoal(goal: TGoal) {
	const img = (value: string) => `/img/svg/anamnesis/goal-${value}.svg`;

	switch (goal) {
		case 'fruits':
			return img('lose-weight');
		case 'running':
			return img('get-fitter');
		case 'healthier':
			return img('live-healthier');
		default:
			return img('lose-weight');
	}
}

export default class HealthGoalSelection extends React.Component<HealthGoalSelectionProps> {
	public static defaultProps = {
		onGoalChange: noop,
		bmiValue: -1,
		isPregnant: false,
	};

	public render() {
		const { goals } = this.props;

		return (
			<div className="m-health-goal">
				<ol className="m-health-goal__list">{goals.map(this.renderGoal)}</ol>
				{this.renderExplanation()}
			</div>
		);
	}

	private getExplanationLabel(type: ExplanationLabelKeys) {
		return i18n(`react.impulsCoach.components.HealthGoalSelection.explanation.${this.props.selectedGoal}.${type}`);
	}

	private renderExplanation() {
		const { selectedGoal, isPregnant, bmiValue } = this.props;

		if (!selectedGoal) {
			return null;
		}
		const goal = this.props.goals.find(
			(currentGoal) => currentGoal.name === this.props.selectedGoal
		) as HealthGoalItem;
		const { isDisabled } = goal.goalType;
		const isUnderweight = isUnderWeightByBMI(bmiValue);

		if (isDisabled) {
			const disabledLabelKey = isPregnant ? 'pregnant.text' : isUnderweight ? 'underweight.text' : null;

			return (
				disabledLabelKey !== null && (
					<div className="m-health-goal__explanation">
						<div className="m-health-goal__info">
							<p className="m-health-goal__info-text">{this.getExplanationLabel(disabledLabelKey)}</p>
						</div>
					</div>
				)
			);
		}

		return (
			<div className="m-health-goal__explanation">
				<div className="m-health-goal__info">
					<p className="m-health-goal__info-text">{this.getExplanationLabel('title')}</p>
				</div>
				<div className="m-health-goal__info">
					<h3 className="m-health-goal__info-title">
						<Icon iconName={this.getExplanationLabel('item1.iconName')} />
						&nbsp;{this.getExplanationLabel('item1.title')}
					</h3>
					<p className="m-health-goal__info-text">{this.getExplanationLabel('item1.text')}</p>
				</div>
				<div className="m-health-goal__info">
					<h3 className="m-health-goal__info-title">
						<Icon iconName={this.getExplanationLabel('item2.iconName')} />
						&nbsp;{this.getExplanationLabel('item2.title')}
					</h3>
					<p className="m-health-goal__info-text">{this.getExplanationLabel('item2.text')}</p>
				</div>
			</div>
		);
	}

	private renderGoal = (goal: HealthGoalItem, index: number) => {
		const { name, text } = goal;

		const listClasses = ['m-health-goal__item'];

		const { isRecommended, isSelected, isDisabled } = goal.goalType;
		if (isSelected) {
			listClasses.push('state-m-health-goal__item--selected');
		}

		if (isDisabled) {
			listClasses.push('state-m-health-goal__item--disabled');
		}

		const variableClickWrapper = () => {
			this.props.onGoalChange(name);
		};

		return (
			<li key={index} className={listClasses.join(' ')} onClick={variableClickWrapper}>
				<div className="m-health-goal__bubble">
					<div className="m-health-goal__back" />
					<UIAsset className={`m-health-goal__image`} src={getImagePathFromGoal(name)} alt={text} />
				</div>
				{isRecommended && (
					<div className="m-health-goal__label">
						<span className="m-health-goal__label-text">
							{i18n('react.impulsCoach.components.HealthGoalSelection.recommendedBanner')}
						</span>
					</div>
				)}
				<p className="m-health-goal__text">
					<span className="m-health-goal__text-content">{text}</span>
				</p>
			</li>
		);
	};
}
